// client/src/lib/inventory.js
// Inventory display logic, mirroring server/lib/inventory.js.
//
// Kept in sync manually — server is CommonJS, client is ESM, so we can't
// share the file directly. The two-tier policy and freshness rules must
// stay aligned with the server version, or the UI and DB will disagree.
//
// TWO-TIER POLICY (decided May 2026):
//   - in_stock     → green badge ("In Stock")
//   - out_of_stock → red badge ("Out of Stock")
//   - unknown      → gray badge ("Stock Unknown") — for products without
//                    inventory data, e.g. PSRESTful rows or inventory_supports=false
//
// Manufactured / buy-to-order items with zero count are treated as in_stock
// on the server side (the summary upstream of this function does that
// reduction), so we don't redo it here — we just trust inventory_status.

/**
 * Compute the inventory display for a product row.
 * Returns null if the supplier doesn't support inventory at all (badge hidden).
 *
 * Shape: { state, label, color, freshness, quantity }
 *   state    — 'in_stock' | 'out_of_stock' | 'unknown'
 *   label    — short text for badge
 *   color    — semantic key ('green' | 'red' | 'gray') matching InventoryBadge's BADGE_COLORS
 *   freshness — human "Checked Xm ago" string for tooltip / label
 *   quantity — raw count if known, else null
 */
export function getInventoryDisplay(product) {
  if (!product || !product.inventory_supports) return null;

  const qty = product.inventory_quantity;
  let state = product.inventory_status;
  if (state !== 'in_stock' && state !== 'out_of_stock') {
    if (typeof qty === 'number' && qty > 0) state = 'in_stock';
    else if (typeof qty === 'number' && qty <= 0) state = 'out_of_stock';
    else state = 'unknown';
  }

  let label, color;
  switch (state) {
    case 'in_stock':     label = 'In Stock';      color = 'green'; break;
    case 'out_of_stock': label = 'Out of Stock';  color = 'red';   break;
    default:             label = 'Stock Unknown'; color = 'gray';
  }

  return {
    state,
    label,
    color,
    freshness: humanizeFreshness(product.inventory_checked_at),
    quantity: typeof qty === 'number' ? qty : null,
  };
}

/**
 * Convert an ISO timestamp into a short "Checked X ago" string.
 * Returns "Never checked" for null/undefined.
 */
export function humanizeFreshness(checkedAt) {
  if (!checkedAt) return 'Never checked';
  const then = new Date(checkedAt);
  if (Number.isNaN(then.getTime())) return 'Unknown';

  const ms = Date.now() - then.getTime();
  const minutes = Math.round(ms / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return then.toLocaleDateString();
}

/**
 * Whether the product is eligible for live refresh.
 * Direct integrations (Gemline, PCNA, Magnet, HPG family) support live refresh.
 * HPG brands all carry data_source like 'direct_hpg_<slug>' — match the
 * prefix so all 8 HPG brands count without enumerating each one here.
 * PSRESTful products don't have a direct supplier integration; the refresh
 * button is shown disabled with explanatory text for those rows.
 */
export function canRefreshLive(product) {
  if (!product) return false;
  const ds = product.data_source;
  return ds === 'direct_gemline'
      || ds === 'direct_pcna'
      || ds === 'direct_magnet'
      || ds === 'direct_sanmar'
      || ds === 'direct_ssactivewear'
      || (typeof ds === 'string' && ds.startsWith('direct_hpg_'));
}

/**
 * Map a product's data_source to the supplier path used in /api/<supplier>/inventory/refresh.
 * For HPG brands, the path is /api/hpg/<slug>/inventory/refresh — slug recovered
 * from the data_source suffix (e.g. 'direct_hpg_hubpen' → 'hpg/hubpen').
 * Returns null if the product isn't refresh-eligible.
 */
export function supplierPathForRefresh(product) {
  const ds = product?.data_source;
  switch (ds) {
    case 'direct_gemline': return 'gemline';
    case 'direct_pcna':    return 'pcna';
    case 'direct_magnet':  return 'magnet';
    case 'direct_sanmar':       return 'sanmar';
    case 'direct_ssactivewear': return 'ssactivewear';
    default:
      if (typeof ds === 'string' && ds.startsWith('direct_hpg_')) {
        return `hpg/${ds.slice('direct_hpg_'.length)}`;
      }
      return null;
  }
}

/**
 * Strip the supplier-code prefix to recover the supplier's native productId.
 * Row id format: '<SUPPLIER_CODE>-<productId>'  (e.g. 'MAG-1080' → '1080').
 */
export function nativeProductId(product) {
  if (!product?.id) return null;
  const dash = product.id.indexOf('-');
  if (dash < 0) return product.id;
  return product.id.slice(dash + 1);
}
